import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { BlockDef } from "./block.interfaces";
import { arrayField, createBlock, headingField, htmlField } from "./blockBuilder";
import { useDefaultColors } from "./useDefaultColors";
import {
    CenteredContact,
    type CenteredContactProps,
    SimpleCentered,
    type SimpleCenteredProps,
    SideBySideGrid,
    type SideBySideGridProps,
    SimpleFourColumn,
    type SimpleFourColumnProps,
    SplitWithImage,
    type SplitWithImageProps,
    SplitWithPattern,
    type SplitWithPatternProps,
    WithTestimonial,
    type WithTestimonialProps,
} from "@/views/blocks/contact/contact";
import { useFieldConfig } from "./useFieldConfig";
import {
    AVATAR_API_BASE_URL,
    getStockPhotoUrl,
    ICON_API_BASE_URL,
    LOGO_API_BASE_URL,
} from "../page.service";

export function useContact() {
    const { t } = useI18n();
    const { colorDefaults } = useDefaultColors();
    const { translations } = useFieldConfig();

    const centeredContactBlock = computed<BlockDef>(() =>
        createBlock<CenteredContactProps>({
            typeId: "CenteredContact",
            type: "contact",
            name: t("page.blocks.contact.centered"),
            component: CenteredContact,
        })
            .withField(
                "title",
                headingField(translations.value.title, "Contact Us", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.",
                    translations.value.description
                )
            )
            .withArrayField(
                "fields",
                arrayField(
                    translations.value.field,
                    "formFields",
                    {
                        label: { label: translations.value.label, type: "text" },
                        placeholder: { label: translations.value.placeholder, type: "text" },
                        inputName: { label: translations.value.inputName, type: "text" },
                        isRequired: { label: translations.value.isRequired, type: "switch" },
                        type: {
                            label: translations.value.type,
                            type: "select",
                            options: [
                                { value: "text", label: translations.value.fieldType_text },
                                { value: "email", label: translations.value.fieldType_email },
                                { value: "tel", label: translations.value.fieldType_tel },
                                {
                                    value: "textarea",
                                    label: translations.value.fieldType_textarea,
                                },
                            ],
                        },
                        spanFull: { label: translations.value.spanFull, type: "switch" },
                    },
                    [
                        {
                            label: "Company",
                            placeholder: "Enter your company name",
                            inputName: "company",
                            isRequired: false,
                            type: "text",
                            spanFull: true,
                        },
                        {
                            label: "Email",
                            placeholder: "Enter your email",
                            inputName: "email",
                            isRequired: true,
                            type: "email",
                            spanFull: true,
                        },
                        {
                            label: "Phone number",
                            placeholder: "Enter your phone number",
                            inputName: "phone-number",
                            isRequired: false,
                            type: "tel",
                            spanFull: true,
                        },
                        {
                            label: "Message",
                            placeholder: "How can we help you?",
                            inputName: "message",
                            isRequired: true,
                            type: "textarea",
                            spanFull: true,
                        },
                    ],
                    {
                        label: "New field",
                        placeholder: "Enter your field name",
                        inputName: "new-field",
                        isRequired: false,
                        type: "text",
                        spanFull: true,
                    }
                )
            )
            .withField("privacyPolicy", {
                label: translations.value.privacyPolicy,
                group: "options",
                fields: [
                    {
                        value: "text",
                        type: "text",
                        label: translations.value.text,
                    },
                    {
                        value: "linkText",
                        type: "text",
                        label: translations.value.urlLabel,
                    },
                    {
                        value: "href",
                        type: "text",
                        label: translations.value.href,
                    },
                    {
                        value: "validationMessage",
                        type: "text",
                        label: translations.value.validationMessage,
                    },
                ],
                defaultData: {
                    data: {
                        text: "By submitting this form you agree to our",
                        linkText: "privacy policy",
                        href: "#",
                        validationMessage: "Please agree to the privacy policy to continue.",
                    },
                    isHidden: false,
                },
            })
            .withField("submitButton", {
                label: translations.value.submitButton,
                group: "buttons",
                fields: [
                    {
                        value: "label",
                        type: "text",
                        label: translations.value.label,
                    },
                    {
                        value: "hookUrl",
                        type: "text",
                        label: translations.value.hookUrl,
                    },
                    {
                        value: "errorMessage",
                        type: "text",
                        label: translations.value.errorMessage,
                    },
                    {
                        value: "successMessage",
                        type: "text",
                        label: translations.value.successMessage,
                    },
                ],
                defaultData: {
                    data: {
                        label: "Send message",
                        hookUrl: "#",
                        errorMessage: "Something went wrong. Please contact us directly via email.",
                        successMessage: "Message sent successfully. We'll get back to you soon.",
                    },
                    isHidden: false,
                },
            })
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const simpleCenteredContactBlock = computed<BlockDef>(() =>
        createBlock<SimpleCenteredProps>({
            typeId: "SimpleCentered",
            type: "contact",
            name: t("page.blocks.contact.simpleCentered"),
            component: SimpleCentered,
        })
            .withField(
                "title",
                headingField(translations.value.title, "Get in touch", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Contact our team for help with any questions about our products.",
                    translations.value.description
                )
            )
            .withArrayField(
                "contacts",
                arrayField(
                    translations.value.contact,
                    "contacts",
                    {
                        iconSrc: { label: translations.value.iconSrc, type: "image" },
                        title: { label: translations.value.title, type: "text" },
                        description: { label: translations.value.description, type: "text" },
                        linkText: { label: translations.value.linkLabel, type: "text" },
                        linkUrl: { label: translations.value.linkHref, type: "text" },
                    },
                    [
                        {
                            iconSrc: `${ICON_API_BASE_URL}/alerts_and_feedback/notification-box.svg`,
                            title: "Customer care",
                            description:
                                "Our team is ready to help with your inquiries and provide solutions for your business needs.",
                            linkText: "Reach out",
                            linkUrl: "#",
                        },
                        {
                            iconSrc: `${ICON_API_BASE_URL}/communication/message-alert-circle.svg`,
                            title: "Issue tracking",
                            description:
                                "If you encounter any problems, let us know so we can address them quickly and improve your experience.",
                            linkText: "Submit issue",
                            linkUrl: "#",
                        },
                        {
                            iconSrc: `${ICON_API_BASE_URL}/communication/annotation.svg`,
                            title: "Support center",
                            description:
                                "Access our knowledge base and connect with specialists for technical guidance and assistance.",
                            linkText: "Visit support",
                            linkUrl: "#",
                        },
                    ],
                    {
                        iconSrc: `${ICON_API_BASE_URL}/communication/mail-01.svg`,
                        title: "Contact item",
                        description: "Contact item description",
                        linkText: "Contact link text",
                        linkUrl: "#",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const sideBySideGridBlock = computed<BlockDef>(() =>
        createBlock<SideBySideGridProps>({
            typeId: "SideBySideGrid",
            type: "contact",
            name: t("page.blocks.contact.sideBySideGrid"),
            component: SideBySideGrid,
        })
            .withField(
                "contactsTitle",
                headingField(translations.value.title, "Contact Us", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "contactsDescription",
                htmlField(
                    translations.value.description,
                    "Reach out to our team for support, inquiries, or partnership opportunities.",
                    translations.value.text
                )
            )
            .withArrayField(
                "contactCards",
                arrayField(
                    translations.value.contact,
                    "contacts",
                    {
                        title: { label: translations.value.title, type: "text" },
                        email: { label: translations.value.email, type: "text" },
                        phone: { label: translations.value.phone, type: "text" },
                    },
                    [
                        {
                            title: "Partnerships",
                            email: "partners@example.com",
                            phone: "+1 (800) 123-4567",
                        },
                        {
                            title: "Media",
                            email: "media@example.com",
                            phone: "+1 (800) 234-5678",
                        },
                        {
                            title: "Careers",
                            email: "jobs@example.com",
                            phone: "+1 (800) 345-6789",
                        },
                        {
                            title: "Support",
                            email: "support@example.com",
                            phone: "+1 (800) 456-7890",
                        },
                    ],
                    {
                        title: "New contact",
                        email: "contact@example.com",
                        phone: "+1 (800) 000-0000",
                    }
                )
            )
            .withField(
                "locationsTitle",
                headingField(translations.value.title, "Our Locations", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "locationsDescription",
                htmlField(
                    translations.value.description,
                    "Visit our global offices to meet our team and experience our culture firsthand.",
                    translations.value.description
                )
            )
            .withArrayField(
                "locationCards",
                arrayField(
                    translations.value.location,
                    "locations",
                    {
                        city: { label: translations.value.city, type: "text" },
                        addressLine1: { label: translations.value.addressLine1, type: "text" },
                        addressLine2: { label: translations.value.addressLine2, type: "text" },
                    },
                    [
                        {
                            city: "San Francisco",
                            addressLine1: "123 Tech Avenue",
                            addressLine2: "San Francisco, CA 94105",
                        },
                        {
                            city: "Boston",
                            addressLine1: "456 Innovation Drive",
                            addressLine2: "Boston, MA 02110",
                        },
                        {
                            city: "London",
                            addressLine1: "789 Digital Lane",
                            addressLine2: "London, UK EC2A 4NE",
                        },
                        {
                            city: "Singapore",
                            addressLine1: "321 Future Street",
                            addressLine2: "Singapore, 018956",
                        },
                    ],
                    {
                        city: "New Location",
                        addressLine1: "789 Main Street",
                        addressLine2: "City, State ZIP",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const simpleFourColumnBlock = computed<BlockDef>(() =>
        createBlock<SimpleFourColumnProps>({
            typeId: "SimpleFourColumn",
            type: "contact",
            name: t("page.blocks.contact.simpleFourColumn"),
            component: SimpleFourColumn,
        })
            .withField(
                "title",
                headingField(translations.value.title, "Find an Office Near You", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Visit us at one of our office locations or reach out with any questions. Our team is ready to assist you at any of our branches nationwide.",
                    translations.value.description
                )
            )
            .withArrayField(
                "locations",
                arrayField(
                    translations.value.contact,
                    "contacts",
                    {
                        city: { label: translations.value.city, type: "text" },
                        addressFirstLine: { label: translations.value.addressLine1, type: "text" },
                        addressSecondLine: { label: translations.value.addressLine2, type: "text" },
                    },
                    [
                        {
                            city: "Los Angeles",
                            addressFirstLine: "8721 Sunset Boulevard",
                            addressSecondLine: "Los Angeles, CA 90069",
                        },
                        {
                            city: "New York",
                            addressFirstLine: "350 Fifth Avenue",
                            addressSecondLine: "New York, NY 10118",
                        },
                        {
                            city: "San Francisco",
                            addressFirstLine: "1 Market Street",
                            addressSecondLine: "San Francisco, CA 94105",
                        },
                        {
                            city: "Chicago",
                            addressFirstLine: "233 S Wacker Drive",
                            addressSecondLine: "Chicago, IL 60606",
                        },
                    ],
                    {
                        city: "New Location",
                        addressFirstLine: "123 Main Street",
                        addressSecondLine: "City, State ZIP",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const splitWithImageBlock = computed<BlockDef>(() =>
        createBlock<SplitWithImageProps>({
            typeId: "SplitWithImage",
            type: "contact",
            name: t("page.blocks.contact.splitWithImage"),
            component: SplitWithImage,
        })
            .withField(
                "title",
                headingField(translations.value.title, "Contact Us", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.",
                    translations.value.description
                )
            )
            .withArrayField(
                "formFields",
                arrayField(
                    translations.value.field,
                    "formFields",
                    {
                        label: { label: translations.value.label, type: "text" },
                        placeholder: { label: translations.value.placeholder, type: "text" },
                        inputName: { label: translations.value.inputName, type: "text" },
                        isRequired: { label: translations.value.isRequired, type: "switch" },
                        type: {
                            label: translations.value.type,
                            type: "select",
                            options: [
                                { value: "text", label: translations.value.fieldType_text },
                                { value: "email", label: translations.value.fieldType_email },
                                { value: "tel", label: translations.value.fieldType_tel },
                                { value: "textarea", label: translations.value.fieldType_textarea },
                            ],
                        },
                        spanFull: { label: translations.value.spanFull, type: "switch" },
                    },
                    [
                        {
                            label: "Company",
                            placeholder: "Enter your company name",
                            inputName: "company",
                            isRequired: false,
                            type: "text",
                            spanFull: true,
                        },
                        {
                            label: "Email",
                            placeholder: "Enter your email",
                            inputName: "email",
                            isRequired: true,
                            type: "email",
                            spanFull: true,
                        },
                        {
                            label: "Phone number",
                            placeholder: "Enter your phone number",
                            inputName: "phone-number",
                            isRequired: false,
                            type: "tel",
                            spanFull: true,
                        },
                        {
                            label: "Message",
                            placeholder: "How can we help you?",
                            inputName: "message",
                            isRequired: true,
                            type: "textarea",
                            spanFull: true,
                        },
                    ],
                    {
                        label: "New field",
                        placeholder: "Enter your field name",
                        inputName: "new-field",
                        isRequired: false,
                        type: "text",
                        spanFull: true,
                    }
                )
            )
            .withField("privacyPolicy", {
                label: translations.value.privacyPolicy,
                group: "options",
                fields: [
                    {
                        value: "text",
                        type: "text",
                        label: translations.value.text,
                    },
                    {
                        value: "linkText",
                        type: "text",
                        label: translations.value.urlLabel,
                    },
                    {
                        value: "href",
                        type: "text",
                        label: translations.value.href,
                    },
                    {
                        value: "validationMessage",
                        type: "text",
                        label: translations.value.validationMessage,
                    },
                ],
                defaultData: {
                    data: {
                        text: "By submitting this form you agree to our",
                        linkText: "privacy policy",
                        href: "#",
                        validationMessage: "Please agree to the privacy policy to continue.",
                    },
                    isHidden: false,
                },
            })
            .withField("submitButton", {
                label: translations.value.submitButton,
                group: "buttons",
                fields: [
                    {
                        value: "label",
                        type: "text",
                        label: translations.value.label,
                    },
                    {
                        value: "hookUrl",
                        type: "text",
                        label: translations.value.hookUrl,
                    },
                    {
                        value: "errorMessage",
                        type: "text",
                        label: translations.value.errorMessage,
                    },
                    {
                        value: "successMessage",
                        type: "text",
                        label: translations.value.successMessage,
                    },
                ],
                defaultData: {
                    data: {
                        label: "Send message",
                        hookUrl: "#",
                        errorMessage: "Something went wrong. Please contact us directly via email.",
                        successMessage: "Message sent successfully. We'll get back to you soon.",
                    },
                    isHidden: false,
                },
            })
            .withField("image", {
                label: translations.value.image,
                group: "images",
                fields: [
                    {
                        value: "src",
                        type: "image",
                        label: translations.value.image,
                    },
                    {
                        value: "alt",
                        type: "text",
                        label: translations.value.alt,
                    },
                ],
                defaultData: {
                    data: {
                        src: getStockPhotoUrl(
                            "webinars",
                            "close-up-smiley-woman-wearing-headphones"
                        ),
                        alt: "Contact us",
                    },
                    isHidden: false,
                },
            })
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const splitWithPatternBlock = computed<BlockDef>(() =>
        createBlock<SplitWithPatternProps>({
            typeId: "SplitWithPattern",
            type: "contact",
            name: t("page.blocks.contact.splitWithPattern"),
            component: SplitWithPattern,
        })
            .withField(
                "title",
                headingField(translations.value.title, "Contact Us Today", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "We're here to help with any questions you might have about our products and services. Fill out the form and our team will get back to you as soon as possible.",
                    translations.value.description
                )
            )
            .withField("address", {
                label: translations.value.address,
                group: "texts",
                fields: [
                    {
                        value: "text",
                        type: "text",
                        label: translations.value.text,
                    },
                ],
                defaultData: {
                    data: {
                        text: "123 Innovation Center, Silicon Valley, CA 94024",
                    },
                    isHidden: false,
                },
            })
            .withField("phone", {
                label: translations.value.phone,
                group: "buttons",
                fields: [
                    {
                        value: "label",
                        type: "text",
                        label: translations.value.label,
                    },
                    {
                        value: "href",
                        type: "text",
                        label: translations.value.href,
                    },
                ],
                defaultData: {
                    data: {
                        label: "+1 (800) 555-1234",
                        href: "tel:+1 (800) 555-1234",
                    },
                    isHidden: false,
                },
            })
            .withField("email", {
                label: translations.value.email,
                group: "buttons",
                fields: [
                    {
                        value: "label",
                        type: "text",
                        label: translations.value.label,
                    },
                    {
                        value: "href",
                        type: "text",
                        label: translations.value.href,
                    },
                ],
                defaultData: {
                    data: {
                        label: "support@yourcompany.com",
                        href: "mailto:support@yourcompany.com",
                    },
                    isHidden: false,
                },
            })
            .withArrayField(
                "formFields",
                arrayField(
                    translations.value.field,
                    "formFields",
                    {
                        label: { label: translations.value.label, type: "text" },
                        placeholder: { label: translations.value.placeholder, type: "text" },
                        inputName: { label: translations.value.inputName, type: "text" },
                        isRequired: { label: translations.value.isRequired, type: "switch" },
                        type: {
                            label: translations.value.type,
                            type: "select",
                            options: [
                                { value: "text", label: translations.value.fieldType_text },
                                { value: "email", label: translations.value.fieldType_email },
                                { value: "tel", label: translations.value.fieldType_tel },
                                { value: "textarea", label: translations.value.fieldType_textarea },
                            ],
                        },
                        spanFull: { label: translations.value.spanFull, type: "switch" },
                    },
                    [
                        {
                            label: "Company",
                            placeholder: "Enter your company name",
                            inputName: "company",
                            isRequired: false,
                            type: "text",
                            spanFull: true,
                        },
                        {
                            label: "Email",
                            placeholder: "Enter your email",
                            inputName: "email",
                            isRequired: true,
                            type: "email",
                            spanFull: true,
                        },
                        {
                            label: "Phone number",
                            placeholder: "Enter your phone number",
                            inputName: "phone-number",
                            isRequired: false,
                            type: "tel",
                            spanFull: true,
                        },
                        {
                            label: "Message",
                            placeholder: "How can we help you?",
                            inputName: "message",
                            isRequired: true,
                            type: "textarea",
                            spanFull: true,
                        },
                    ],
                    {
                        label: "New field",
                        placeholder: "Enter your field name",
                        inputName: "new-field",
                        isRequired: false,
                        type: "text",
                        spanFull: true,
                    }
                )
            )
            .withField("privacyPolicy", {
                label: translations.value.privacyPolicy,
                group: "options",
                fields: [
                    {
                        value: "text",
                        type: "text",
                        label: translations.value.text,
                    },
                    {
                        value: "linkText",
                        type: "text",
                        label: translations.value.urlLabel,
                    },
                    {
                        value: "href",
                        type: "text",
                        label: translations.value.href,
                    },
                    {
                        value: "validationMessage",
                        type: "text",
                        label: translations.value.validationMessage,
                    },
                ],
                defaultData: {
                    data: {
                        text: "By submitting this form you agree to our",
                        linkText: "privacy policy",
                        href: "#",
                        validationMessage: "Please agree to the privacy policy to continue.",
                    },
                    isHidden: false,
                },
            })
            .withField("submitButton", {
                label: translations.value.submitButton,
                group: "buttons",
                fields: [
                    {
                        value: "label",
                        type: "text",
                        label: translations.value.label,
                    },
                    {
                        value: "hookUrl",
                        type: "text",
                        label: translations.value.hookUrl,
                    },
                    {
                        value: "errorMessage",
                        type: "text",
                        label: translations.value.errorMessage,
                    },
                    {
                        value: "successMessage",
                        type: "text",
                        label: translations.value.successMessage,
                    },
                ],
                defaultData: {
                    data: {
                        label: "Send message",
                        hookUrl: "#",
                        errorMessage: "Something went wrong. Please contact us directly via email.",
                        successMessage: "Message sent successfully. We'll get back to you soon.",
                    },
                    isHidden: false,
                },
            })
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const withTestimonialBlock = computed<BlockDef>(() =>
        createBlock<WithTestimonialProps>({
            typeId: "WithTestimonial",
            type: "contact",
            name: t("page.blocks.contact.withTestimonial"),
            component: WithTestimonial,
        })
            .withField(
                "title",
                headingField(translations.value.title, "Contact Us today", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Our support specialists are ready to assist you with any inquiries about our platform and services.",
                    translations.value.text
                )
            )
            .withArrayField(
                "formFields",
                arrayField(
                    translations.value.field,
                    "formFields",
                    {
                        label: { label: translations.value.label, type: "text" },
                        placeholder: { label: translations.value.placeholder, type: "text" },
                        inputName: { label: translations.value.inputName, type: "text" },
                        isRequired: { label: translations.value.isRequired, type: "switch" },
                        type: {
                            label: translations.value.type,
                            type: "select",
                            options: [
                                { value: "text", label: translations.value.fieldType_text },
                                { value: "email", label: translations.value.fieldType_email },
                                { value: "tel", label: translations.value.fieldType_tel },
                                { value: "textarea", label: translations.value.fieldType_textarea },
                            ],
                        },
                        spanFull: { label: translations.value.spanFull, type: "switch" },
                    },
                    [
                        {
                            label: "First name",
                            type: "text",
                            placeholder: "Enter your first name",
                            isRequired: true,
                            spanFull: false,
                            inputName: "first-name",
                        },
                        {
                            label: "Last name",
                            type: "text",
                            placeholder: "Enter your last name",
                            isRequired: true,
                            spanFull: false,
                            inputName: "last-name",
                        },
                        {
                            label: "Email",
                            placeholder: "Enter your email",
                            inputName: "email",
                            isRequired: true,
                            type: "email",
                            spanFull: true,
                        },
                        {
                            label: "Phone number",
                            placeholder: "Enter your phone number",
                            inputName: "phone-number",
                            isRequired: false,
                            type: "tel",
                            spanFull: true,
                        },
                        {
                            label: "Message",
                            placeholder: "How can we help you?",
                            inputName: "message",
                            isRequired: true,
                            type: "textarea",
                            spanFull: true,
                        },
                    ],
                    {
                        label: "New field",
                        placeholder: "Enter your field name",
                        inputName: "new-field",
                        isRequired: false,
                        type: "text",
                        spanFull: true,
                    }
                )
            )
            .withField("privacyPolicy", {
                label: translations.value.privacyPolicy,
                group: "options",
                fields: [
                    {
                        value: "text",
                        type: "text",
                        label: translations.value.text,
                    },
                    {
                        value: "linkText",
                        type: "text",
                        label: translations.value.urlLabel,
                    },
                    {
                        value: "href",
                        type: "text",
                        label: translations.value.href,
                    },
                    {
                        value: "validationMessage",
                        type: "text",
                        label: translations.value.validationMessage,
                    },
                ],
                defaultData: {
                    data: {
                        text: "By submitting this form you agree to our",
                        linkText: "privacy policy",
                        href: "#",
                        validationMessage: "Please agree to the privacy policy to continue.",
                    },
                    isHidden: false,
                },
            })
            .withField("submitButton", {
                label: translations.value.submitButton,
                group: "buttons",
                fields: [
                    {
                        value: "label",
                        type: "text",
                        label: translations.value.label,
                    },
                    {
                        value: "hookUrl",
                        type: "text",
                        label: translations.value.hookUrl,
                    },
                    {
                        value: "errorMessage",
                        type: "text",
                        label: translations.value.errorMessage,
                    },
                    {
                        value: "successMessage",
                        type: "text",
                        label: translations.value.successMessage,
                    },
                ],
                defaultData: {
                    data: {
                        label: "Send message",
                        hookUrl: "#",
                        errorMessage: "Something went wrong. Please contact us directly via email.",
                        successMessage: "Message sent successfully. We'll get back to you soon.",
                    },
                    isHidden: false,
                },
            })
            .withField("testimonial", {
                label: translations.value.testimonial,
                group: "texts",
                fields: [
                    {
                        value: "logoImage",
                        type: "image",
                        label: translations.value.logo,
                    },
                    {
                        value: "text",
                        type: "text",
                        label: translations.value.text,
                    },
                    {
                        value: "personImage",
                        type: "text",
                        label: translations.value.image,
                    },
                    {
                        value: "personName",
                        type: "text",
                        label: translations.value.name,
                    },
                    {
                        value: "personPosition",
                        type: "text",
                        label: translations.value.position,
                    },
                ],
                defaultData: {
                    data: {
                        logoImage: `${LOGO_API_BASE_URL}/1`,
                        text: "The team at Example Corp provided exceptional support and guidance throughout our implementation process. Their expertise and dedication made all the difference.",
                        personImage: `${AVATAR_API_BASE_URL}/m/1`,
                        personName: "John Smith",
                        personPosition: "CEO, Company Name",
                    },
                    isHidden: false,
                },
            })
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    return {
        blocks: computed(() => [
            centeredContactBlock.value,
            simpleCenteredContactBlock.value,
            sideBySideGridBlock.value,
            simpleFourColumnBlock.value,
            splitWithImageBlock.value,
            splitWithPatternBlock.value,
            withTestimonialBlock.value,
        ]),
    };
}
